import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class TestingToolkitService {
    private readonly STORAGE_KEY = 'x2x_testing_date';
    private testDateSubject = new BehaviorSubject<Date | null>(null);

    /** Observable for components to subscribe to test date changes */
    testDate$ = this.testDateSubject.asObservable();

    constructor() {
        this.loadFromStorage();
    }

    /**
     * Load saved test date from localStorage on service initialization
     */
    private loadFromStorage(): void {
        try {
            const savedDate = localStorage.getItem(this.STORAGE_KEY);
            if (savedDate) {
                const parsedDate = new Date(savedDate);
                if (!isNaN(parsedDate.getTime())) {
                    this.testDateSubject.next(parsedDate);
                }
            }
        } catch (error) {
            console.warn('Failed to load test date from storage:', error);
        }
    }

    /**
     * Get the current test date, or null if not set
     */
    getTestDate(): Date | null {
        return this.testDateSubject.value;
    }

    /**
     * Set a new test date (or clear it with null)
     */
    setTestDate(date: Date | null): void {
        this.testDateSubject.next(date);

        if (date) {
            localStorage.setItem(this.STORAGE_KEY, date.toISOString());
        } else {
            localStorage.removeItem(this.STORAGE_KEY);
        }
    }

    /**
     * Get the current date as ISO string - uses test date if set, otherwise returns new Date()
     * This is the main method to use in API calls
     */
    getCurrentDateISO(): string {
        const testDate = this.testDateSubject.value;
        return testDate ? testDate.toISOString() : new Date().toISOString();
    }

    /**
     * Get the current date - uses test date if set, otherwise returns new Date()
     */
    getCurrentDate(): Date {
        return this.testDateSubject.value || new Date();
    }

    /**
     * Check if a test date is currently enabled
     */
    isEnabled(): boolean {
        return this.testDateSubject.value !== null;
    }

    /**
     * Clear the test date
     */
    clearTestDate(): void {
        this.setTestDate(null);
    }

    /**
     * Format date for display (YYYY-MM-DD format for date inputs)
     */
    formatDateForInput(date: Date | null): string {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Parse date from input (YYYY-MM-DD format)
     */
    parseDateFromInput(dateString: string): Date | null {
        if (!dateString) return null;
        const date = new Date(dateString + 'T00:00:00.000Z');
        return isNaN(date.getTime()) ? null : date;
    }
}
